import { useMemo } from 'react'
import { collection, query, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { useBlockedUsers } from '../contexts/BlockedUsersContext'
import { openSlots } from '@shared/eventTickets'
import type { EventTickets } from '../types'

export type TicketActivityKind = 'for_you' | 'open' | 'seeking'

export interface TicketActivity {
  key: string
  kind: TicketActivityKind
  eventId: string
  /** The crew member on the other end: who has a ticket for you, has an open one, or is looking. */
  userId: string
  at: number
  /** The other person's ledger — what the feed's actions answer or claim on. */
  ledger: EventTickets
  /** The viewer's own ledger for that event ('seeking': the open ticket to hand over). */
  mine?: EventTickets
}

const millis = (t?: { toMillis(): number } | null) => (t ? t.toMillis() : 0)

/**
 * Ticket things waiting on the viewer, across every event: a ticket assigned to
 * them, an open ticket while they're looking, and someone looking while they
 * have an open one. Each lasts until it's resolved, not for a time window.
 */
export function useTicketActivity(uid: string | null): { items: TicketActivity[]; loading: boolean } {
  const mineRef = useMemo(
    () => (uid ? (query(collection(db, 'eventTickets'), where('userId', '==', uid)) as Query<EventTickets>) : null),
    [uid],
  )
  const crewRef = useMemo(
    () => (uid ? (query(collection(db, 'eventTickets'), where('viewerIds', 'array-contains', uid)) as Query<EventTickets>) : null),
    [uid],
  )
  const mine = useFirestoreCollection<EventTickets>(mineRef)
  const crew = useFirestoreCollection<EventTickets>(crewRef)
  const { isBlocked } = useBlockedUsers()

  const items = useMemo(() => {
    if (!uid) return []
    const others = crew.data.filter(l => l.userId !== uid && !isBlocked(l.userId))
    const out: TicketActivity[] = []
    const add = (kind: TicketActivityKind, l: EventTickets, m?: EventTickets) =>
      out.push({ key: `ticket:${kind}:${l.id}`, kind, eventId: l.eventId, userId: l.userId, at: millis(l.updatedAt), ledger: l, mine: m })
    for (const l of others) if ((l.pendingIds ?? []).includes(uid)) add('for_you', l)
    for (const m of mine.data) {
      const iHaveOpen = openSlots(m).length > 0
      for (const l of others) {
        if (l.eventId !== m.eventId) continue
        if (m.seeking && openSlots(l).length > 0) add('open', l)
        if (iHaveOpen && l.seeking) add('seeking', l, m)
      }
    }
    return out
  }, [uid, mine.data, crew.data, isBlocked])

  return { items, loading: mine.loading || crew.loading }
}
